import { OrderType } from 'types/cart.types';
import { SQL_DATE_FORMAT } from 'api/api.constant';
import { format, parse } from 'date-fns';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';

export const DATE_START_TIME_FORMAT = 'h:mm a';
export const MILLISECONDS_IN_MINUTE = 60000;
export const EASTERN_TIME = 'America/New_York';
const HOURS_12 = 12;
const MINUTES_10 = 10;

export function formatToAmPmTime(time: string): string {
  return format(new Date(time), DATE_START_TIME_FORMAT);
}

export function zonedTime(time: string): string {
  return formatInTimeZone(time, EASTERN_TIME, DATE_START_TIME_FORMAT);
}

/**
 * Extracts and formats a given date string into a readable string format showing the abbreviated month and full year.
 *
 * @param dateString - A string representation of the date, e.g., "2023-09-18T05:00:00.000Z"
 * @returns A formatted date string, e.g., "September, 2023"
 */
export const extractMonthYear = (dateString: Date): string => {
  return format(new Date(dateString), 'MMMM, yyyy');
};

/**
 * Return string of Shorten Month, Calendar Date, Calendar Year
 * @param date - Date string
 * @returns Sep 18, 2023
 */
export function formatMonthDateYear(date: Date): string {
  return format(date, 'PP');
}

/**
 * Return string of Shorten Day of week, shorten Month, Calendar Date, Calendar Year
 * @param dateToConvert - Date string
 * @returns Mon, Sep 18, 2023
 */
export function formatToReadableDateWithoutTime(dateToConvert: Date): string {
  return `${format(dateToConvert, 'eee')}, ${format(dateToConvert, 'PP')}`;
}
export function dateCheckIfIncludesTime(dateStr: string): Date {
  const isTimeNotInclude = dateStr.includes('T00:00');
  if (isTimeNotInclude) {
    return new Date(dateStr.replace('Z', ''));
  }
  return new Date(dateStr);
}

/**
 * Return string of Shorten Day of week, Shorten Month, Calendar Date, Calendar Year, Time
 * @param dateToConvert - Date string
 * @returns Mon, Sep 18, 2023 at 1:00 AM
 */
export function formatToReadableDate(dateToConvert: Date): string {
  return `${format(new Date(dateToConvert), 'eee')}, ${format(
    new Date(dateToConvert),
    'PP',
  )} at ${format(new Date(dateToConvert), 'p')}`;
}

/**
 * Return string of Shorten Month, Calendar Date, Calendar Year, Time
 * @param dateToConvert - Date string
 * @returns Sep 18, 2023 1:00 AM
 */
export function orderStatusDate(dateToConvert: Date): string {
  return `${format(new Date(dateToConvert), 'PP')} ${format(
    new Date(dateToConvert),
    'p',
  )}`;
}

/**
 * Return timezone offset in minutes as number
 * @param timeZone - Timezone string
 * @returns minutes as number
 */
export function getTimezoneOffseMinutes(timeZone: string): number {
  return getTimezoneOffset(timeZone) / MILLISECONDS_IN_MINUTE;
}

/**
 * Return hours from date ISO string as number
 * @param isoDateString - ISO date string
 * @returns hours as number which is between characters 11 and 13
 */
export const getHoursFromISODateString = (isoDateString: string): number =>
  Number(isoDateString.slice(0, 2));

/**
 * Return minutes from date ISO string as number
 * @param isoDateString - ISO date string
 * @returns hours as number which is between characters 14 and 16
 */
export const getMinutesFromISODateString = (isoDateString: string): number =>
  Number(isoDateString.slice(3, 5));

/**
 * Converts date to sql date string format
 * @param date - date
 * @returns SQLDateString yyyy-mm-dd
 */
export const dateToSQLDateString = (date: Date): string =>
  format(date, 'yyyy-MM-dd');

/**
 * Converts date to sql date time string format
 * @param date - date
 * @returns SQLDateTimeString yyyy-mm-dd hh:mm:ss
 */
export const dateToSQLDateTimeString = (date: Date): string =>
  format(date, 'yyyy-MM-dd HH:mm:ss');

/**
 * Converts date to long localized time format
 * @param date - date
 * @returns string 02:00 PM
 */
export const getLocalizedTime = (date: Date): string => format(date, 'p');

/**
 * Converts date to legacy date format string
 * @param date - date
 * @returns string 04/14/2022
 */
export const formatToLegacyDateString = (date: Date): string =>
  format(date, 'MM/dd/yyyy');

/**
 * Converts sql date string to date
 * @param string 2022-09-06
 * @returns date
 */
export const parseSqlDate = (s: string): Date =>
  parse(s, SQL_DATE_FORMAT, new Date());

/**
 * Converts date to expiry date format string
 * @param date - date
 * @returns string 04/2022
 */
export const formatToExpiryDate = (date: Date): string =>
  format(new Date(date), 'MM/yyyy');

/**
 * Converts date to time format in AM/PM
 * @param date - Date
 * @returns string 07:30 PM
 */
export const formatAMPM = (date: Date): string => {
  let hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const ampm = hours >= HOURS_12 ? 'PM' : 'AM';

  hours %= HOURS_12;
  hours = hours || HOURS_12;
  const convertedMinutes = minutes < MINUTES_10 ? `0${minutes}` : minutes;
  const strTime = `${hours}:${convertedMinutes} ${ampm}`;

  return strTime;
};

export function formatToReadableDateTimeFormatAMPM(
  dateToConvert: Date,
): string {
  return `${format(new Date(dateToConvert), 'eee')}, ${format(
    new Date(dateToConvert),
    'PP',
  )} at ${formatAMPM(dateToConvert)} `;
}

function getNextDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
}

export function isNextDate(date: Date): boolean {
  const tommarow = getNextDay(new Date());
  const givenDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  return givenDate >= tommarow;
}

function parseTimeString(timeStr: string): {
  hours: number;
  minutes: number;
} {
  const [time, modifier] = timeStr.split(' ');
  // eslint-disable-next-line prefer-const
  let [hours, minutes] = time.split(':').map(Number);

  if (modifier.toLowerCase() === 'pm' && hours < 12) {
    hours += 12;
  }
  if (modifier.toLowerCase() === 'am' && hours === 12) {
    hours = 0; // 12 AM is 00:00
  }

  return { hours, minutes };
}

function formatDate(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}`;
}

export function getFulfillmentTimes(
  fulfillmentDate: string,
  deliveryTime: string,
  isNextHour = false,
): string {
  const { hours, minutes } = parseTimeString(deliveryTime);

  const start = new Date(fulfillmentDate);
  start.setHours(isNextHour ? hours + 1 : hours, minutes, 0, 0);

  return formatDate(start);
}
function convertMilitaryTo12Hour(militaryTime: string): {
  hours: number;
  minutes: number;
  period: 'AM' | 'PM';
} {
  const [hours24, minutes] = militaryTime.split(':').map(Number);

  const period = hours24 >= 8 && hours24 <= 11 ? 'AM' : 'PM';

  let hours12 = hours24;
  if (hours24 === 0) {
    hours12 = 12;
  } else if (hours24 > 12) {
    hours12 = hours24 - 12;
  }

  return { hours: hours12, minutes, period };
}

function formatTime12Hour(
  hours: number,
  minutes: number,
  period: string,
): string {
  const paddedMinutes = minutes.toString().padStart(2, '0');
  return `${hours}:${paddedMinutes} ${period}`;
}

function extractTimeFromISO(isoString: string): string {
  return isoString.split('T')[1].split('.')[0].slice(0, 5);
}

export function createFulfillmentTimeRange(
  fulfillmentTimeFrom: string,
  fulfillmentTimeTo: string,
  orderType: OrderType,
): string {
  if (orderType !== 1) return '';
  if (!fulfillmentTimeFrom || !fulfillmentTimeTo) return '';

  const fromMilitaryTime = extractTimeFromISO(fulfillmentTimeFrom);
  const toMilitaryTime = extractTimeFromISO(fulfillmentTimeTo);

  const fromTime = convertMilitaryTo12Hour(fromMilitaryTime);
  const toTime = convertMilitaryTo12Hour(toMilitaryTime);

  const fromFormatted = formatTime12Hour(
    fromTime.hours,
    fromTime.minutes,
    fromTime.period,
  );
  const toFormatted = formatTime12Hour(
    toTime.hours,
    toTime.minutes,
    toTime.period,
  );

  return `${fromFormatted} - ${toFormatted}`;
}

export function formatTimeRange(timeStr: string): string {
  const parts = timeStr.split(' - ');

  const formatTime = (timeToFormat: string): string => {
    const regex = /(\d+)(AM|PM)/i;
    const match = regex.exec(timeToFormat);
    if (match) {
      const hour = match[1];
      const period = match[2].toUpperCase();
      return `${hour}:00 ${period}`;
    }
    return timeToFormat;
  };

  const start = formatTime(parts[0]);
  const end = formatTime(parts[1]);

  return `${start}-\n       ${end}`;
}
